import { BehaviorSubject, Observable, take, filter, forkJoin, of, catchError, throwError, timeout } from 'rxjs';
import audioLogTypeEnum from './AudioLogTypeEnum.mjs';

/**
 * @description Manages all aspects of device audio, including playback and talkback
 * @module Media.AudioService
 */
function createAudioService(apiService, logService, environment, _window, util) {
    const moduleName = 'audioService';
    const audioDevices = {};
    const createMediaSessionTimeoutMs = 5000;
    const createWebSocketTimeoutMs = 5000;
    let audioContext;
    let audioWorker;
    let talkbackDeviceId;
    let source;
    let processor;

    function log(params) {
        params.module = moduleName;
        logService.log(params);
        return params;
    }

    function handleAutoPlay(value) {
        // inform all audioDevice listeners that audio has resumed
        Object.values(audioDevices).forEach(audioDevice => {
            audioDevice?.info?.next({
                type: audioLogTypeEnum.autoPlay,
                value: value,
                deviceId: audioDevice.deviceId
            });
        });
    }

    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/AudioContext}
     * @returns {AudioContext}
     */
    function getAudioContext() {
        // if there was already an audioContext, resume if it was suspended or interrupted
        // https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/state
        if (audioContext?.state === 'suspended' || audioContext?.state === 'interrupted') {
            audioContext.resume().then(
                () => {
                    handleAutoPlay(true);
                },
                () => {
                    handleAutoPlay(false);
                }
            );
        }
        if (!audioContext) {
            const windowAudioContext = _window.AudioContext ?? _window.webkitAudioContext;
            if (windowAudioContext) {
                audioContext = new windowAudioContext();
                handleAutoPlay(audioContext.state === 'running');
            }
        }
        return audioContext;
    }

    function getUserMedia() {
        if (_window.navigator.getUserMedia) {
            return _window.navigator.getUserMedia;
        }
        if (_window.navigator.webkitGetUserMedia) {
            return _window.navigator.webkitGetUserMedia;
        }
        if (_window.navigator.mozGetUserMedia) {
            return _window.navigator.mozGetUserMedia;
        }
    }

    function playBuffer(e) {
        const audioDevice = audioDevices[e.target.deviceId];
        if (audioDevice) {
            const length = audioDevice.buffer.length;

            // buffer 1 second before starting to play, but once playing starts, keep playing
            if (length > 0 && !audioDevice.isDoingTalkback) {
                audioDevice.playing = true;

                // Dynamically set playback speed so that the buffer won't run out
                let time = length / audioDevice.sampleRate;
                let playSpeed;

                if (time > 2.5) {
                    // buffer too big - play faster to catch up
                    playSpeed = 1.05;
                } else if (time > 0.8) {
                    // buffer in normal range - play normal speed
                    playSpeed = 1;
                } else if (time > 0.6) {
                    // buffer too small - slow down playback
                    playSpeed = 0.96;
                } else {
                    // buffer very small - slow playback more
                    playSpeed = 0.92;
                }
                // If the buffer is drained playback will stop until the buffer fills up again.

                // take at most half a second of buffer
                let pcmFloat = audioDevice.buffer.splice(
                    0,
                    Math.min(length, audioDevice.sampleRate * 0.5)
                );
                const ctx = getAudioContext();
                const audioBuffer = ctx.createBuffer(1, pcmFloat.length, audioDevice.sampleRate);
                audioBuffer.getChannelData(0).set(pcmFloat);
                let source = ctx.createBufferSource();
                source.deviceId = e.target.deviceId;
                source.buffer = audioBuffer;
                source.connect(ctx.destination);
                source.playbackRate.value = playSpeed;
                audioDevice.source = source;
                source.addEventListener('ended', playBuffer, false);
                source.start();
            } else {
                audioDevice.playing = false;
            }
        }
    }

    /**
     * @description A single WebWorker does audio for all devices,
     *     but the worker create a socket for each device
     * @returns {WebWorker}
     */
    function getAudioWorker() {
        if (!_window.Worker) {
            log({
                code: audioLogTypeEnum.workersNotSupported
            });
            return;
        }

        if (!audioWorker) {
            audioWorker = new _window.Worker(environment.audioWorkerUrl);

            audioWorker.onmessage = function(e) {
                const data = e.data;
                const audioDevice = audioDevices[data.deviceId];

                if (audioDevice) {
                    if (data.data) {
                        const audioData = Array.from(new Float32Array(data.buffer));
                        audioDevice.buffer = audioDevice.buffer.concat(audioData);
                        audioDevice.sampleRate = data.sampleRate;
                        let secondsOfBuffer = audioDevice.buffer.length / audioDevice.sampleRate;

                        // during talkback, keep adding new data to the buffer,
                        // but limit buffer size by removing the oldest data
                        if (audioDevice.isDoingTalkback && secondsOfBuffer > 2) {
                            audioDevice.buffer.splice(
                                0,
                                (secondsOfBuffer - 2) * audioDevice.sampleRate
                            );
                        }

                        // start playing when 1 second of audio has buffered, not currently playing, and not talkback
                        if (secondsOfBuffer > 1 && !audioDevice.playing && !audioDevice.isDoingTalkback) {
                            playBuffer({ target: { deviceId: data.deviceId }});
                        }

                    } else if (data.error) {
                        // forward error messages to clients
                        audioDevice.info.error({
                            type: data.type,
                            error: data.error,
                            deviceId: data.deviceId,
                            module: moduleName,
                            traceId: audioDevice?.traceId,
                            timeSinceStart: _window.Date.now() - audioDevice?.startTime
                        });
                        log({
                            code: data.type,
                            error: data.error,
                            deviceId: data.deviceId,
                            traceId: audioDevice?.traceId,
                            int: _window.Date.now() - audioDevice?.startTime
                        });
                        removeAudioDevice(audioDevice);
                    } else if (data.type) {
                        // forward success messages to clients
                        audioDevice.info.next({
                            type: data.type,
                            deviceId: audioDevice.deviceId,
                            module: moduleName,
                            traceId: audioDevice?.traceId,
                            timeSinceStart: _window.Date.now() - audioDevice?.startTime
                        });

                        if (data.type === audioLogTypeEnum.socketInitConfirmed) {
                            util.startMedia(
                                audioDevice,
                                apiService,
                                audioLogTypeEnum,
                                log,
                                'AUDIO'
                            );
                        }
                    }
                }

            };
        }
        return audioWorker;
    }

    function userMediaFailed(e) {
        const audioDevice = audioDevices[talkbackDeviceId];
        if (audioDevice) {
            // do not error here since the stream can continue without talkback
            audioDevice.info.next({
                type: audioLogTypeEnum.talkbackError,
                descr: 'getUserMedia failed',
                deviceId: audioDevice.deviceId,
                error: e,
                module: moduleName,
                traceId: audioDevice?.traceId,
                timeSinceStart: _window.Date.now() - audioDevice?.startTime
            });
        }
    }

    function startAudioRecorder(deviceId) {
        talkbackDeviceId = deviceId;
        if (getUserMedia()) {
            getUserMedia()({ audio: true }, startUserMedia, userMediaFailed);
        } else {
            const audioDevice = audioDevices[talkbackDeviceId];
            if (audioDevice) {
                // do not error here since the stream can continue without talkback
                audioDevice.info.next({
                    type: audioLogTypeEnum.talkbackError,
                    error: new Error('getUserMedia not supported or the environment is insecure'),
                    deviceId: audioDevice.deviceId,
                    module: moduleName,
                    traceId: audioDevice?.traceId,
                    timeSinceStart: _window.Date.now() - audioDevice?.startTime
                });
            }
        }
    }

    /**
     * @description This is called once the user authorizes access to the microphone
     * @param {Stream} stream
     */
    function startUserMedia(stream) {

        // Stop audio playing back from this device immediately
        // by stopping the currently playing source.
        audioDevices[talkbackDeviceId].isDoingTalkback = true;
        const currertlyPlayingSource = audioDevices[talkbackDeviceId].source;
        if (currertlyPlayingSource) {
            currertlyPlayingSource.stop();
            delete audioDevices[talkbackDeviceId].source;
        }

        source = getAudioContext().createMediaStreamSource(stream);
        processor = getAudioContext().createScriptProcessor(
            1024, /*buffer size*/
            1, /* input channels */
            1 /* output channels */
        );
        source.connect(processor);
        processor.connect(getAudioContext().destination);
        processor.onaudioprocess = function(e) {
            // e.inputBuffer is an AudioBuffer with properties:
            //     length: 1024,
            //     duration: length in seconds of the buffer
            //     sampleRate - for example, 48000
            //     numberOfChannels - usually 1
            // e.inputBuffer.getChannelData(channel) - a method with channel usually 0
            //    returns the actual data in a buffer as a Float32Array

            let buffer = Float32Array.from(e.inputBuffer.getChannelData(0)).buffer;

            getAudioWorker().postMessage(
                {
                    command: 'send',
                    deviceId: talkbackDeviceId,
                    buffer: buffer,
                    length: e.inputBuffer.length,
                    duration: e.inputBuffer.duration,
                    sampleRate: e.inputBuffer.sampleRate,
                    numberOfChannels: e.inputBuffer.numberOfChannels
                },
                [
                    buffer
                ]
            );
        };
    }

    function stopAudioRecorder() {
        delete audioDevices[talkbackDeviceId].isDoingTalkback;
        talkbackDeviceId = undefined;
        processor.disconnect();
        source.disconnect();
        source = undefined;
        processor.onaudioprocess = undefined;
        processor = undefined;
    }

    function removeAudioDevice(audioDevice) {
        if (audioDevice) {
            _window.clearInterval(audioDevice.keepAlive);
            audioDevice.keepAlive = undefined;
            if (getAudioWorker()) {
                getAudioWorker().postMessage({
                    command: 'close',
                    deviceId: audioDevice.deviceId
                });
            }
        }
    }

    function socketIsOpen(value) {
        return value && value.type === audioLogTypeEnum.socketOpenConfirmed;
    }

    function initStream(audioDevice) {

        getAudioWorker().postMessage({
            command: 'open',
            deviceId: audioDevice.deviceId,
            opusScriptUrl: environment.libopusScriptUrl,
            webSocketBase: environment.webSocketBase
        });

        // Open a viewerSession and a socket in parallel for best performance,
        // then send the init command to start streaming
        forkJoin({
            viewerSession: util.createMediaSession(
                audioDevice,
                apiService,
                audioLogTypeEnum
            ).pipe(
                timeout({
                    each: createMediaSessionTimeoutMs,
                    with: () => throwError(() => new Error('create viewerSession timed out'))
                }),
                catchError(e => of({ isError: true, error: e }))
            ),
            newSocket: audioDevice.info.pipe(
                filter(socketIsOpen),
                take(1),
                timeout({
                    each: createWebSocketTimeoutMs,
                    with: () => throwError(() => new Error('create WebSocket timed out'))
                }),
                catchError(e => of({ isError: true, error: e }))
            )
        }).subscribe({
            next(result) {
                if (result?.viewerSession?.isError) {
                    initStreamViewerSessionError(audioDevice, result.viewerSession.error);
                    return;
                }
                if (result?.newSocket?.isError) {
                    initStreamWebSocketError(audioDevice, result.newSocket.error);
                    return;
                }

                getAudioWorker().postMessage({
                    command: 'start',
                    deviceId: audioDevice.deviceId,
                    viewerSession: result.viewerSession
                });
                audioDevice.keepAlive = util.getKeepalive(audioDevice, apiService, audioLogTypeEnum);
            }
        });
    }

    function initStreamWebSocketError(audioDevice, webSocketError) {
        audioDevice.info.error({
            type: audioLogTypeEnum.socketOpenFailed,
            error: webSocketError,
            deviceId: audioDevice?.deviceId,
            module: moduleName,
            traceId: audioDevice?.traceId,
            timeSinceStart: _window.Date.now() - audioDevice?.startTime
        });
        log({
            code: audioLogTypeEnum.socketOpenFailed,
            deviceId: audioDevice.deviceId,
            int: _window.Date.now() - audioDevice.startTime,
            error: webSocketError,
            traceId: audioDevice?.traceId
        });
        removeAudioDevice(audioDevice);
    }

    function initStreamViewerSessionError(audioDevice, viewerSessionError) {
        // inform the client why the session failed
        if (viewerSessionError?.response?.status === 401) {
            // user was using bad credentials
            audioDevice.info.next({
                type: audioLogTypeEnum.notAuthorized,
                error: viewerSessionError,
                deviceId: audioDevice?.deviceId,
                module: moduleName,
                timeSinceStart: _window.Date.now() - audioDevice.startTime,
                traceId: audioDevice?.traceId
            });
        } else if (Array.isArray(viewerSessionError?.response?.data?.errors)) {
            viewerSessionError.response.data.errors.forEach(e => {
                if (e.code === 'camera_offline') {
                    // the device was actually offline
                    audioDevice.info.next({
                        type: audioLogTypeEnum.offline,
                        error: viewerSessionError,
                        deviceId: audioDevice?.deviceId,
                        module: moduleName,
                        timeSinceStart: _window.Date.now() - audioDevice.startTime,
                        traceId: audioDevice?.traceId
                    });
                }
            });
        }

        audioDevice.info.error({
            type: audioLogTypeEnum.viewerSessionFailed,
            error: viewerSessionError,
            deviceId: audioDevice?.deviceId,
            module: moduleName,
            traceId: audioDevice?.traceId,
            timeSinceStart: _window.Date.now() - audioDevice?.startTime
        });
        log({
            code: audioLogTypeEnum.viewerSessionFailed,
            deviceId: audioDevice.deviceId,
            int: _window.Date.now() - audioDevice.startTime,
            error: viewerSessionError,
            traceId: audioDevice?.traceId
        });
        removeAudioDevice(audioDevice);
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @description Starts audio for a given device
     * @param {String} deviceId
     * @param {String} [traceId]
     * @returns {Observable} Emits data about audio streaming
     * @name start
     * @method
     */
    function start(deviceId, traceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'startBadParams',
                    object: deviceId,
                    traceId: traceId,
                    deviceId: deviceId,
                    module: moduleName
                })
            ));
        }
        if (!audioDevices[deviceId]) {
            audioDevices[deviceId] = {
                buffer: [],
                info: new BehaviorSubject({
                    type: audioLogTypeEnum.init,
                    deviceId: deviceId
                }),
                deviceId: deviceId,
                ready: undefined,
                openPromise: undefined,
                resolveOpenPromise: undefined,
                rejectOpenPromise: undefined,
                startPromise: undefined,
                resolveStartPromise: undefined,
                rejectStartPromise: undefined,
                started: undefined,
                playing: undefined,
                source: undefined,
                isDoingTalkback: undefined,
                keepAlive: undefined,
                viewerSession: undefined,
                startTime: _window.Date.now(),
                traceId: traceId ?? util.randomBase64Code(15)
            };

            // the setTimeout forces the BehaviorSubject to emit its first value mseLogTypeEnum.init
            // before doing anything else
            _window.setTimeout(
                () => {
                    initStream(audioDevices[deviceId]);
                },
                1
            );
        } else {
            // if there is already an audioDevice, this will resume audio
            // in case the AudioContext is suspended
            getAudioContext();
        }
        return audioDevices[deviceId].info.asObservable();
    }

    /**
     * @description Stops audio for a given device
     * @param {String} deviceId The device to stop audio for
     * @returns {Observable} Completes when the socket is closed
     * @name stop
     * @method
     */
    function stop(deviceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            return new Observable(subscriber => {
                subscriber.error({
                    type: audioLogTypeEnum.badParams,
                    module: moduleName
                });
            });
        }

        // request to close the socket
        if (audioDevices[deviceId]) {
            removeAudioDevice(audioDevices[deviceId]);
        }

        // emit and clean up after the socket is closed
        return new Observable(subscriber => {
            audioDevices[deviceId].info.asObservable()
                .pipe(filter(x => x.type === audioLogTypeEnum.socketClosed), take(1)).subscribe({
                    next: () => {
                        delete audioDevices[deviceId];
                        subscriber.next({
                            type: audioLogTypeEnum.socketClosed,
                            deviceId: deviceId
                        });
                        subscriber.complete();
                    },
                    error: e => {
                        delete audioDevices[deviceId];
                        subscriber.error({
                            type: audioLogTypeEnum.socketClosed,
                            deviceId: deviceId,
                            error: e,
                            module: moduleName
                        });
                    }
                });
        });
    }

    /**
     * @description Start sending talkback audio for a given device
     * @param {String} deviceId The device to start sending audio for
     * @name startSend
     * @method
     */
    function startSend(deviceId) {
        if (getAudioWorker()) {
            getAudioWorker().postMessage({
                command: 'startSend',
                viewerSession: '', //viewerSessionId,
                deviceId: deviceId
            });
            startAudioRecorder(deviceId);
        }
    }

    // TODO: inclomplete
    /*
     * @description Stop sending talkback audio
     * @param {*} deviceId
     * @param {*} viewerSessionId
     * @name stopSend
     * @method
     */

    /**
     * @description Stop sending talkback audio for a given device
     * @param {String} deviceId The device to stop sending audio for
     * @name stopSend
     * @method
     */
    function stopSend(deviceId) {
        if (getAudioWorker()) {
            getAudioWorker().postMessage({
                command: 'stopSend',
                viewerSession: '', //viewerSessionId,
                deviceId: deviceId
            });
            stopAudioRecorder();
        }
    }

    // TODO: incomplete
    // _audioService.resume = function() {
    // };

    return Object.freeze({
        /** @property {module:AudioService.AudioLogTypeEnum} logTypeEnum */
        get logTypeEnum() { return audioLogTypeEnum; },
        start: start,
        stop: stop,
        startSend: startSend,
        stopSend: stopSend
    });
}

export default createAudioService;
